import { useEffect, useState } from "react";
import { Plus, Download, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { generatePrescriptionPDF } from "../lib/pdf";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import MedicationCombobox from "../components/MedicationCombobox";
import VademecumPicker, { InteractionsBanner } from "../components/VademecumPicker";

const empty = { name: "", dosage: "", frequency: "", duration: "", instructions: "" };

export default function Prescriptions() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", diagnosis: "", notes: "", medications: [empty] });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSyms, setAiSyms] = useState("");
  const [aiAge, setAiAge] = useState("");
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get("/prescriptions").then(({ data }) => setItems(data));

  useEffect(() => {
    load();
    api.get("/medications").then(({ data }) => setCatalog(data)).catch(() => {});
    if (user?.role === "doctor") {
      api.get("/patients")
        .then(({ data }) => setPatients(data.map((p) => ({ id: p.id, name: p.name }))))
        .catch(() => {
          api.get("/appointments").then(({ data }) => {
            const map = {};
            data.forEach((a) => { if (a.patient_id) map[a.patient_id] = a.patient_name; });
            setPatients(Object.entries(map).map(([id, name]) => ({ id, name })));
          }).catch(() => {});
        });
    }
  }, [user]);

  const setMed = (i, k, v) => setForm((p) => {
    const m = [...p.medications]; m[i] = { ...m[i], [k]: v }; return { ...p, medications: m };
  });
  const addMed = () => setForm((p) => ({ ...p, medications: [...p.medications, empty] }));
  const removeMed = (i) => setForm((p) => ({ ...p, medications: p.medications.filter((_, j) => j !== i) }));

  const applyCatalog = (i, item) => {
    const found = typeof item === "string" ? catalog.find((c) => c.name === item) : item;
    if (!found) {
      const name = typeof item === "string" ? item : item?.name || "";
      return setMed(i, "name", name);
    }
    setForm((p) => {
      const m = [...p.medications];
      m[i] = { name: found.name, dosage: found.default_dosage, frequency: found.default_frequency, duration: found.default_duration, instructions: m[i].instructions || "" };
      return { ...p, medications: m };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/prescriptions", form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ patient_id: "", diagnosis: "", notes: "", medications: [empty] });
      load();
      generatePrescriptionPDF(data);
    } catch (err) { toast.error(formatError(err)); }
  };

  const aiSuggest = async () => {
    setAiLoading(true);
    setAiText("");
    try {
      const { data } = await api.post("/ai/suggest", { symptoms: aiSyms, patient_age: aiAge ? parseInt(aiAge) : undefined });
      setAiText(data.suggestion);
    } catch (err) { toast.error(formatError(err)); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="prescriptions-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl">{t("prescriptions")}</h1>
        {user?.role === "doctor" && (
          <div className="flex gap-2">
            <Dialog open={aiOpen} onOpenChange={setAiOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="ai-assist-btn"><Sparkles className="w-4 h-4 mr-2" /> {t("ai_assist")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("ai_assist")}</DialogTitle>
                  <DialogDescription>Asistente de sugerencias basado en IA. No sustituye el juicio clínico.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t("symptoms")}</Label><Textarea rows={4} value={aiSyms} onChange={(e) => setAiSyms(e.target.value)} data-testid="ai-symptoms" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("age")}</Label><Input type="number" value={aiAge} onChange={(e) => setAiAge(e.target.value)} data-testid="ai-age" /></div>
                  </div>
                  <Button onClick={aiSuggest} disabled={aiLoading || !aiSyms} className="bg-primary hover:bg-primary/90 w-full" data-testid="ai-suggest-btn">
                    {aiLoading ? "..." : t("suggest")}
                  </Button>
                  {aiText && (
                    <div className="bg-[#F3F1EC] rounded-lg p-3 text-sm whitespace-pre-wrap" data-testid="ai-result">{aiText}</div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="new-prescription-btn"><Plus className="w-4 h-4 mr-2" /> {t("new_prescription")}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t("new_prescription")}</DialogTitle>
                  <DialogDescription>Se generará un PDF firmado electrónicamente con QR de verificación.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <Label>{t("patient")}</Label>
                    <Select value={form.patient_id} onValueChange={(v) => setForm((p) => ({ ...p, patient_id: v }))}>
                      <SelectTrigger data-testid="rx-patient"><SelectValue placeholder={t("select_patient")} /></SelectTrigger>
                      <SelectContent>
                        {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("diagnosis")}</Label><Textarea required rows={2} value={form.diagnosis} onChange={(e) => setForm((p) => ({ ...p, diagnosis: e.target.value }))} data-testid="rx-diagnosis" /></div>
                  <div className="space-y-2">
                    <Label>{t("medications")}</Label>
                    {form.medications.map((m, i) => (
                      <div key={i} className="border border-border rounded-lg p-3 space-y-2">
                        <div className="flex gap-2">
                          <VademecumPicker
                            value={m.name}
                            onChange={(v) => setMed(i, "name", v)}
                            onSelect={(item) => {
                              setForm((p) => {
                                const arr = [...p.medications];
                                arr[i] = { ...arr[i], name: item.nombre, active: (item.principiosActivos || [])[0] || "" };
                                return { ...p, medications: arr };
                              });
                            }}
                            placeholder="Buscar en Vademécum CIMA-AEMPS..."
                            testid={`rx-med-${i}-name`}
                          />
                          {form.medications.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeMed(i)} data-testid={`rx-med-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input placeholder={t("dosage")} value={m.dosage} onChange={(e) => setMed(i, "dosage", e.target.value)} data-testid={`rx-med-${i}-dosage`} />
                          <Input placeholder={t("frequency")} value={m.frequency} onChange={(e) => setMed(i, "frequency", e.target.value)} data-testid={`rx-med-${i}-frequency`} />
                          <Input placeholder={t("duration")} value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} data-testid={`rx-med-${i}-duration`} />
                        </div>
                        <Input placeholder={t("instructions")} value={m.instructions} onChange={(e) => setMed(i, "instructions", e.target.value)} data-testid={`rx-med-${i}-instructions`} />
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addMed} data-testid="rx-med-add"><Plus className="w-4 h-4 mr-1" /> {t("add")}</Button>
                    <InteractionsBanner actives={form.medications.map((m) => (m.active || m.name)).filter(Boolean)} />
                  </div>
                  <div><Label>{t("notes")}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="rx-notes" /></div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="rx-save-btn">{t("save")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display">{t("prescriptions")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
          {items.map((r) => (
            <div key={r.id} className="border border-border rounded-lg p-4 flex flex-wrap justify-between gap-3" data-testid={`rx-${r.id}`}>
              <div>
                <div className="text-xs text-muted-foreground">{r.code} · {new Date(r.created_at).toLocaleString()}</div>
                <div className="font-medium">{r.diagnosis}</div>
                <div className="text-xs text-muted-foreground">{user?.role === "patient" ? r.doctor_name : r.patient_name} · {r.medications.length} med.</div>
              </div>
              <Button variant="outline" size="sm" onClick={async () => generatePrescriptionPDF(r)} data-testid={`rx-pdf-${r.id}`}>
                <Download className="w-4 h-4 mr-1" /> {t("download_pdf")}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
